import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/shared/Card';
import { Badge } from '../components/shared/Badge';
import { Button } from '../components/shared/Button';
import { api } from '../lib/api';
import type { Inspection, TireStatus } from '../types';
import { formatDateShort } from '../lib/utils';

export const Inspections = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TireStatus | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadInspections = async () => {
      try {
        setIsLoading(true);
        const data = await api.getInspections();
        setInspections(data);
      } catch (error) {
        console.error('Failed to load inspections:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInspections();
  }, []);

  const filteredInspections = useMemo(() => {
    return inspections.filter((inspection) => {
      const matchesSearch =
        searchTerm === '' ||
        inspection.vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inspection.vehicle.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${inspection.vehicle.make} ${inspection.vehicle.model}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' || inspection.overallStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [inspections, searchTerm, statusFilter]);

  // Calculate KPIs
  const totalInspections = inspections.length;
  const avgTread =
    inspections.length > 0
      ? (
          inspections.reduce(
            (sum, insp) =>
              sum +
              insp.tires.reduce((tSum, tire) => tSum + tire.tread_mm, 0) /
                insp.tires.length,
            0
          ) / inspections.length
        ).toFixed(1)
      : '0.0';

  const unsafeCount = inspections.filter(
    (insp) => insp.overallStatus === 'unsafe' || insp.overallStatus === 'replace_soon'
  ).length;
  const unsafePercent =
    totalInspections > 0
      ? Math.round((unsafeCount / totalInspections) * 100)
      : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl blur-3xl opacity-10" />
        <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200/50 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                Inspection History
              </h1>
              <p className="text-gray-600 text-lg">
                Browse and analyze past tire inspections with insights
              </p>
            </div>
            <Button onClick={() => navigate('/inspect')} size="lg" className="shadow-lg">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Inspection
            </Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl blur-3xl opacity-10" />
        <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-2xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            Key Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity" />
              <div className="relative bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border border-blue-200 hover:border-blue-400 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/50">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-700 mb-1">Total Inspections</div>
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 bg-clip-text text-transparent">{totalInspections}</div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity" />
              <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-6 border border-emerald-200 hover:border-emerald-400 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/50">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-medium text-emerald-700 mb-1">Avg Tread Depth</div>
                <div className="text-3xl font-bold bg-gradient-to-r from-emerald-700 to-emerald-900 bg-clip-text text-transparent">{avgTread}mm</div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity" />
              <div className="relative bg-gradient-to-br from-amber-50 to-orange-100/50 rounded-2xl p-6 border border-amber-200 hover:border-amber-400 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/50">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-medium text-amber-700 mb-1">Needs Attention</div>
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-700 to-orange-900 bg-clip-text text-transparent">{unsafePercent}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Search
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by VIN, plate, or vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Filter by Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TireStatus | 'all')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="ok">OK</option>
              <option value="watch">Watch</option>
              <option value="replace_soon">Replace Soon</option>
              <option value="unsafe">Unsafe</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredInspections.length} Inspection
            {filteredInspections.length !== 1 ? 's' : ''}
          </h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Loading inspections...</p>
          </div>
        ) : filteredInspections.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 text-gray-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-500 text-lg">No inspections found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Vehicle
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    VIN / Plate
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">
                    Avg Tread
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredInspections.map((inspection) => {
                  const avgTread = (
                    inspection.tires.reduce(
                      (sum, tire) => sum + tire.tread_mm,
                      0
                    ) / inspection.tires.length
                  ).toFixed(1);

                  return (
                    <tr
                      key={inspection.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/inspections/${inspection.id}`)}
                    >
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {formatDateShort(inspection.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {inspection.vehicle.year} {inspection.vehicle.make}{' '}
                        {inspection.vehicle.model}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <div>{inspection.vehicle.vin}</div>
                        <div className="text-xs">{inspection.vehicle.plate}</div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge status={inspection.overallStatus} size="sm" />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {avgTread} mm
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/inspections/${inspection.id}`);
                          }}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
